import React, { createContext, useState, useContext, useEffect } from 'react';

const ChatContext = createContext();

export function ChatProvider({ children }) {
  const [chats, setChats] = useState({});
  const [currentChatId, setCurrentChatId] = useState(null);

  // Load chats from localStorage
  useEffect(() => {
    const savedChats = localStorage.getItem('baba_chats');
    const savedCurrent = localStorage.getItem('baba_current_chat');
    
    if (savedChats) {
      const parsed = JSON.parse(savedChats);
      setChats(parsed);
      
      if (savedCurrent && parsed[savedCurrent]) {
        setCurrentChatId(savedCurrent);
      } else if (Object.keys(parsed).length > 0) {
        setCurrentChatId(Object.keys(parsed)[0]);
      }
    } else {
      // Create first chat
      createNewChat(true);
    }
  }, []);

  // Save chats to localStorage
  useEffect(() => {
    if (Object.keys(chats).length > 0) {
      localStorage.setItem('baba_chats', JSON.stringify(chats));
    }
  }, [chats]);

  // Save current chat ID
  useEffect(() => {
    if (currentChatId) {
      localStorage.setItem('baba_current_chat', currentChatId);
    }
  }, [currentChatId]);

  const createNewChat = (skipSwitch = false) => {
    const id = Date.now().toString();
    const newChat = {
      id,
      name: 'New Chat',
      messages: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    setChats(prev => ({
      ...prev,
      [id]: newChat
    }));
    
    if (!skipSwitch) {
      setCurrentChatId(id);
    } else if (!currentChatId) {
      setCurrentChatId(id);
    }
    
    return id;
  };

  const deleteChat = (id) => {
    const newChats = { ...chats };
    delete newChats[id];
    
    setChats(newChats);
    
    // Switch to another chat if deleting current
    if (id === currentChatId) {
      const remainingIds = Object.keys(newChats);
      if (remainingIds.length > 0) {
        setCurrentChatId(remainingIds[0]);
      } else {
        createNewChat();
      }
    }
  };

  const renameChat = (id, newName) => {
    setChats(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        name: newName,
        updatedAt: new Date().toISOString()
      }
    }));
  };

  const addMessage = (chatId, message) => {
    setChats(prev => ({
      ...prev,
      [chatId]: {
        ...prev[chatId],
        messages: [...prev[chatId].messages, message],
        updatedAt: new Date().toISOString()
      }
    }));
  };

  const updateLastMessage = (chatId, content) => {
    setChats(prev => {
      const chat = prev[chatId];
      const messages = [...chat.messages];
      messages[messages.length - 1] = {
        ...messages[messages.length - 1],
        content
      };
      
      return {
        ...prev,
        [chatId]: {
          ...chat,
          messages,
          updatedAt: new Date().toISOString()
        }
      };
    });
  };

  const clearChat = (id) => {
    setChats(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        messages: [],
        updatedAt: new Date().toISOString()
      }
    }));
  };

  const getCurrentChat = () => {
    return currentChatId ? chats[currentChatId] : null;
  };

  const value = {
    chats,
    currentChatId,
    setCurrentChatId,
    createNewChat,
    deleteChat,
    renameChat,
    addMessage,
    updateLastMessage,
    clearChat,
    getCurrentChat
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
}

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
};
